import { useEffect, useId, useMemo, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { ErroApi } from '../../api';
import {
  campanhaEditavel, emitidoDaVertical, textoDoEncerramento,
  type CampanhaCompleta, type EmitidoDaVertical, type VerticalCompleta, type VerticalDaCampanha,
} from '../../api/cptu';
import {
  GRANDEZAS, MOTOR_CPTU_VERSAO, ZONAS_SBTN, premissasEfetivas, processarVertical,
  type Grandeza, type Resultado, type SugestaoDeCamadas,
} from '../../cptu/motor';
import {
  Abaco, CHAVE_DAS_ESCALAS, FORMULAS_DAS_PREMISSAS, GRUPOS_DA_LEITURA, LeituraNaCota, TRILHAS_PADRAO, Trilhas, chaveDasEscalas,
  comEscala, combinarMarcas, desenhoDoAbaco, gravarEscalas, indiceNaProfundidade, leituras as contaDeLeituras, lerEscalasDe,
  mapaDeIndices, marcaDaColuna, numeroBr,
  type DefinicaoDaTrilha, type DissipacaoNaTrilha, type EscalaDoGrafico, type MarcaDeHomologacao, type TipoDeAbaco,
} from '../../cptu/visual';
import { data, dataDoDia, faixaProfundidade, numero, plural } from '../../formato';
import { useSessao } from '../../sessao';
import { AcaoBloqueada, Aviso, Carregando, Dialogo, ErroDaApi, useEstreito } from '../../ui';
import {
  CamadasDoLado, EstatisticaDasCamadas, TabelaDasCamadas, conferirCamadas, linhasDasVigentes, type LinhaDaCamada,
} from './CamadasDaVertical';
import { motivoDaCampanhaParada, PERMISSAO_DE_EDITAR_CPTU } from './CampanhaMoldura';
import {
  GRUPOS_CORRELACIONADOS, buscaDosParametros, grupoDoEndereco, parametrosDoEndereco, trilhasDoGrupo, type Parametros,
} from './correlacionados';
import { CorrelacaoCruzadaGrafico } from './CorrelacaoCruzadaGrafico';
import { escolhasGravadas, interpretar, useDissipacoesNaTela } from './DissipacaoInterpretada';
import { EdicaoDaVertical, correlacaoDaEdicao, edicaoInteira } from './EdicaoDaVertical';
import { SimularPremissas } from './SimularPremissas';
import { NotasDoPorque, TabelaDaVertical } from './TabelaDaVertical';
import {
  excecoesDaVertical, focarOPrimeiro, leiturasParaOMotor, rotaDaVertical, useFocoDeVolta, useInterpretacao, zonasDaSessao,
  type Rascunho, type VistaDaVertical,
} from './interpretacao';

/**
 * A tela da vertical — onda 313, Tarefa 313.4 (spec 07 §6.1, item 4; a prancheta «1 · Vertical», com as
 * correções R5–R7 e R14; R3, R8, R9, R10).
 *
 * À esquerda, as verticais da campanha (código, profundidade final, NA, dissipações, exceções, camadas e a
 * faixa SBTn das já abertas nesta sessão); no centro, o cabeçalho da vertical (data, operador, o cone e se ele
 * estava apto na data do ensaio, cota, encerramento, «Fotos (n)», que leva à ficha da 312), «Profundidade |
 * Cota», «＋ Trilha», «Simular», «Perfis | Tabela», a faixa do trecho selecionado e as trilhas; à direita, a
 * leitura de todos os parâmetros na cota e o ábaco («SBTn 2010 | SBT | SBTn 2016 | Schneider | Qt1 × Bq»).
 *
 * O resultado é do navegador (R2, `useInterpretacao`). O desenho dizima; o cursor, a leitura, a tabela, o
 * ábaco e a seleção contam todas as leituras (R9): a seleção de trecho anda nas leituras de verdade, e o que
 * se seleciona nas trilhas acende no ábaco, e vice-versa. A leitura na cota recebe o índice do BRUTO.
 *
 * No celular (até 640 px) é consulta (R8): uma coluna só, a lista das verticais vira escolha no topo, as
 * trilhas lado a lado com 120 px no mínimo, quebrando em linhas, a leitura e o ábaco abaixo, nenhum controle de
 * mudança — e a frase que diz onde se muda.
 *
 * Tarefa 313.6 — as vistas Edição e Camadas (spec §5.4 e §5.6; sem prancheta: no padrão desta tela, as trilhas à
 * esquerda com o antes/depois e o formulário à direita):
 *  · Edição (`EdicaoDaVertical`): a edição vai para o MESMO rascunho do «Simular» (premissas, NA, cota, edição), e o
 *    rascunho vale em todas as vistas — as trilhas, a leitura, a tabela e o ábaco mostram o depois, o gravado em cinza
 *    tracejado; fora da vista que o mexe (e com o «Simular» fechado), uma faixa diz o que está no rascunho, com
 *    «Sair sem gravar». O gráfico da correlação cruzada qc × fs fica embaixo das trilhas (o `Plano` da casa pede a
 *    largura do centro para as letras não encolherem);
 *  · Camadas (`CamadasDaVertical`): o rascunho das camadas (uma tabela editável, embaixo das trilhas, com a
 *    estatística por camada) e a sugestão do motor ficam aqui, para valer em todas as vistas: as trilhas desenham as
 *    camadas do rascunho (ou as vigentes) e, nesta vista, as fronteiras sugeridas tracejadas.
 * Nas duas, a lista das verticais vira a escolha no topo (como no celular): o formulário pede o lado, e as trilhas a
 * coluna das unidades — a 1.440 px as cinco trilhas ficam numa linha (rodada da 313.6).
 * Sair da vertical com rascunho (um link da página: as abas, o caminho; ou a escolha da vertical) pede «Sair sem
 * gravar?»; trocar de vista na mesma vertical não perde nada.
 *
 * Onda 315 — Tarefa 315.5b (os pedidos do Humberto de 12/10/2026, vendo o CPeT-IT; e o R10 da 313, plano 315.5):
 *  · «Parâmetros básicos» | «Parâmetros correlacionados» nos Perfis e na Tabela (`correlacionados.ts`): os básicos são
 *    as trilhas de hoje e a tabela Medidos · Corrigidos e normalizados · Classificação; os correlacionados, os grupos
 *    do CPeT-IT e a «Perda de resistência», com a tabela da profundidade, qc, fs e SBTn fixos e as correlações. O
 *    endereço é de cada vista (`?parametros=correlacionados&grupo=2`): o F5 mantém;
 *  · «Colorir pela zona» liga e desliga a cor das trilhas pela zona (o qt pela SBT, o Qtn pela SBTn), lembrado neste
 *    aparelho e ligado por padrão — a regra do PDF, na `Trilhas`;
 *  · as dissipações vão à trilha do u₂ com as três marcas na escala de pressão (● início, ▼ fim, ◆ uᵢ), com os
 *    valores da interpretação gravada de cada uma (`interpretar` da dissipação aberta) — nada novo no motor;
 *  · «Comparar com o emitido»: na campanha com emissão, o antes/depois da 313 com o ANTES = o `processarVertical`
 *    sobre as leituras, as premissas e a edição do pacote da última emissão (`emitidoDaVertical`), e a faixa
 *    «Comparando com o emitido R00 de dd/mm/aaaa»; motor diferente, a frase diz os dois. Sem emissão, o botão fica à
 *    vista, bloqueado com o motivo. Nada se grava.
 */

/** As trilhas que se podem ligar: as cinco padrão e as grandezas do motor (sem a porosidade, que não confere). */
interface OpcaoDeTrilha { chave: string; rotulo: string; detalhe: string | null; series: readonly Grandeza[] }

const NAS_PADRAO: readonly string[] = ['qc', 'qt', 'fs', 'u2', 'u0', 'rf', 'ic'];
const DETALHE_DA_PADRAO: Readonly<Record<string, string>> = {
  qt: 'com o qc', u2: 'com o u₀, o NA e as dissipações', ic: 'com a faixa SBTn',
};

const OPCOES_DE_TRILHA: readonly OpcaoDeTrilha[] = [
  ...TRILHAS_PADRAO.map((t) => ({
    chave: t.chave, rotulo: `${t.titulo}${t.unidade ? ` (${t.unidade})` : ''}`, detalhe: DETALHE_DA_PADRAO[t.chave] ?? null,
    series: t.series.map((s) => s.coluna),
  })),
  ...GRUPOS_DA_LEITURA.flatMap((g) => g.colunas)
    .filter((c): c is Grandeza => c in GRANDEZAS && !NAS_PADRAO.includes(c))
    .map((g) => ({
      chave: g, rotulo: `${GRANDEZAS[g].rotulo}${GRANDEZAS[g].unidade ? ` (${GRANDEZAS[g].unidade})` : ''}`, detalhe: null,
      series: [g],
    })),
];

/** A definição de uma trilha ligada: a padrão, ou uma série só da grandeza (k e cₕ em log). */
function definicaoDaTrilha(chave: string): DefinicaoDaTrilha | null {
  const padrao = TRILHAS_PADRAO.find((t) => t.chave === chave);
  if (padrao) return padrao;
  if (!(chave in GRANDEZAS)) return null;
  const g = chave as Grandeza;
  return {
    chave: g, titulo: GRANDEZAS[g].rotulo, unidade: GRANDEZAS[g].unidade,
    escala: g === 'k' || g === 'ch' ? 'log' : 'linear', series: [{ coluna: g, classe: 'serie-extra' }],
    // O Qtn pinta pela zona SBTn, como no CPeT-IT (o qt, a padrão, pela SBT).
    ...(g === 'qtn' ? { preencherPorZona: 'sbtn' as const } : {}),
  };
}

const CHAVE_DAS_TRILHAS = 'labmanager.cptu.trilhas';
const CHAVE_DA_COR = 'labmanager.cptu.colorirPorZona';

/** «Colorir pela zona» lembrado neste aparelho; ligado por padrão (o pedido do Humberto). */
function lerCor(): boolean {
  try { return localStorage.getItem(CHAVE_DA_COR) !== 'nao'; } catch { return true; }
}

function gravarCor(ligada: boolean): void {
  try { localStorage.setItem(CHAVE_DA_COR, ligada ? 'sim' : 'nao'); } catch { /* o aparelho não guarda */ }
}

/** O motivo do «Comparar com o emitido» bloqueado. */
const SEM_EMISSAO = 'A campanha ainda não foi emitida: não há o que comparar.';

/** «R00», «R01». */
const revisaoEscrita = (n: number): string => `R${String(n).padStart(2, '0')}`;

function lerTrilhas(): string[] {
  try {
    const salvas = JSON.parse(localStorage.getItem(CHAVE_DAS_TRILHAS) ?? 'null');
    if (Array.isArray(salvas)) {
      const validas = salvas.filter((c): c is string => OPCOES_DE_TRILHA.some((o) => o.chave === c));
      if (validas.length > 0) return validas;
    }
  } catch { /* o aparelho não guarda: ficam as padrão */ }
  return TRILHAS_PADRAO.map((t) => t.chave);
}

function gravarTrilhas(chaves: readonly string[]): void {
  try { localStorage.setItem(CHAVE_DAS_TRILHAS, JSON.stringify(chaves)); } catch { /* nada */ }
}

/** Os ábacos, na ordem do seletor. */
const ABACOS: readonly [TipoDeAbaco, string][] = [
  ['sbtn', 'SBTn 2010'], ['sbt', 'SBT'], ['sbtnMod', 'SBTn 2016'], ['schneider', 'Schneider'], ['qtBq', 'Qt1 × Bq'],
];

/** A altura das trilhas no computador: a janela menos o que fica em cima, de 300 a 760 px (R9). */
function useAlturaDasTrilhas(estreito: boolean): number | undefined {
  const [h, setH] = useState(() => (typeof window === 'undefined' ? 900 : window.innerHeight));
  useEffect(() => {
    const ao = () => setH(window.innerHeight);
    window.addEventListener('resize', ao);
    return () => window.removeEventListener('resize', ao);
  }, []);
  return estreito ? undefined : Math.max(300, Math.min(760, h - 220));
}

const m2 = (v: number | null | undefined) => (v === null || v === undefined ? null : `${numeroBr(v, 2)} m`);

export function VerticalInterpretada({ c, verticalId, vista, aoMudar, aoSimular }: {
  c: CampanhaCompleta;
  verticalId: string;
  vista: VistaDaVertical;
  /** A campanha mudou (premissas gravadas): a casca relê. */
  aoMudar(): Promise<void>;
  /** O «Simular» abriu ou fechou, ou a vista tem o «Gravar» dela: a casca deixa um primário só (P-UI-10). */
  aoSimular?(aberto: boolean): void;
}) {
  const estreito = useEstreito();
  const { pode, eu } = useSessao();
  const navegar = useNavigate();
  const it = useInterpretacao(c, verticalId);
  const [eixo, setEixo] = useState<'profundidade' | 'cota'>('profundidade');
  const [cursorZ, setCursorZ] = useState<number | null>(null);
  /** A leitura do bruto que a tabela escolheu (a removida também tem linha); null = a do cursor. */
  const [cursorOrdem, setCursorOrdem] = useState<number | null>(null);
  const [selecao, setSelecao] = useState<{ de: number; ate: number } | null>(null);
  /* O zoom de profundidade das trilhas (onda 347, R13): vale enquanto a tela está aberta; outra vertical (a casca monta
     de novo, pela `key`) ou o F5 voltam à vertical inteira. */
  const [zoom, setZoom] = useState<{ de: number; ate: number } | null>(null);
  const [doAbaco, setDoAbaco] = useState<number[] | null>(null);
  const [tipo, setTipo] = useState<TipoDeAbaco>('sbtn');
  const [ligadas, setLigadas] = useState<string[]>(lerTrilhas);
  const [menu, setMenu] = useState(false);
  const [simulando, setSimulando] = useState(false);
  /** O rascunho das camadas (313.6): null = sem rascunho, valem as vigentes. Fica entre as vistas. */
  const [linhasDasCamadas, setLinhasDasCamadas] = useState<LinhaDaCamada[] | null>(null);
  /**
   * A sugestão de camadas desenhada (313.6): tracejada nas trilhas da vista Camadas até aceitar ou limpar — e se foi
   * calculada sobre o rascunho da interpretação (o que a tela mostrava), para a caixa dizer.
   */
  const [sugestao, setSugestao] = useState<{ s: SugestaoDeCamadas; sobreRascunho: boolean } | null>(null);
  /** Para onde a pessoa quis ir com rascunho: a janela «Sair sem gravar?» (313.6). */
  const [saida, setSaida] = useState<string | null>(null);
  const altura = useAlturaDasTrilhas(estreito);
  const idDoMotivo = useId();
  // «Parâmetros básicos» | «Parâmetros correlacionados» (315): o endereço diz a vista e o grupo; o F5 mantém.
  const [busca] = useSearchParams();
  const parametros: Parametros = parametrosDoEndereco(busca.get('parametros'));
  const grupo = grupoDoEndereco(busca.get('grupo'));
  const [colorir, setColorir] = useState<boolean>(lerCor);
  // A escala horizontal de cada gráfico (onda 347, R12): por grandeza, guardada neste aparelho e por usuário (os
  // celulares do laboratório são compartilhados); não muda o relatório.
  const quem = eu?.usuarioId ?? null;
  const [escalas, setEscalas] = useState<Record<string, EscalaDoGrafico>>(() => lerEscalasDe(CHAVE_DAS_ESCALAS, quem));
  const mudarEscala = (g: string, e: EscalaDoGrafico | null): void => {
    setEscalas((atuais) => {
      const novas = comEscala(atuais, g, e);
      gravarEscalas(chaveDasEscalas(CHAVE_DAS_ESCALAS, quem), novas);
      return novas;
    });
  };
  // As dissipações desta vertical, com a interpretação gravada de cada uma: as marcas na trilha do u₂ (315).
  const daVertical = useDissipacoesNaTela(c, verticalId);
  // «Comparar com o emitido» (315): o pacote da última emissão, lido uma vez por vertical; sem relatório, nem se pede.
  const [emitido, setEmitido] = useState<{ estado: 'lendo' | 'sem' | 'ok' | 'erro'; e?: EmitidoDaVertical; erro?: unknown }>(
    { estado: 'lendo' });
  const [comparando, setComparando] = useState(false);
  // «Sair sem gravar?» devolve o foco a quem a abriu, no «Cancelar» (rodada do agent-browser da 313).
  useFocoDeVolta(saida !== null);

  const v = it.vertical;
  const vistaUsada: VistaDaVertical = vista;
  const comFormulario = vistaUsada === 'edicao' || vistaUsada === 'camadas';
  const bloqueio: 'permissao' | 'parada' | null = !pode('cptu.editar') ? 'permissao'
    : !campanhaEditavel(c.campanha.situacao) ? 'parada' : null;
  const soLer = estreito || !!bloqueio;

  // A casca sabe se há outro primário na tela — o «Gravar» do Simular, da edição ou das camadas — e o «Importar
  // arquivos» vira secundário; ao sair da vertical, volta.
  const outroPrimario = simulando || (comFormulario && !soLer);
  useEffect(() => { aoSimular?.(outroPrimario); }, [outroPrimario, aoSimular]);
  useEffect(() => () => aoSimular?.(false), [aoSimular]);

  const gravado = it.resultado;
  const codigo = v?.codigo ?? null;
  useEffect(() => {
    if (!codigo) return undefined;
    if (!c.campanha.relatorio_id) {
      setEmitido({ estado: 'sem' });
      return undefined;
    }
    let vale = true;
    setEmitido({ estado: 'lendo' });
    emitidoDaVertical(c.campanha.id, codigo).then(
      (e) => { if (vale) setEmitido({ estado: 'ok', e }); },
      (erro) => { if (vale) setEmitido(erro instanceof ErroApi && erro.status === 404 ? { estado: 'sem' } : { estado: 'erro', erro }); },
    );
    return () => { vale = false; };
  }, [c.campanha.id, c.campanha.relatorio_id, codigo]);
  // O antes do «Comparar com o emitido»: o motor da tela sobre as leituras, as premissas e a edição do pacote.
  const doEmitido = useMemo(() => {
    if (emitido.estado !== 'ok' || !emitido.e) return null;
    const e = emitido.e;
    return processarVertical({
      leituras: leiturasParaOMotor(e.leituras as Parameters<typeof leiturasParaOMotor>[0]),
      premissas: premissasEfetivas(e.premissas ?? {}),
      edicao: e.edicao,
    });
  }, [emitido]);
  // Só nos Perfis e na Tabela: na Edição e nas Camadas o antes volta a ser o gravado.
  const comparandoDeFato = comparando && doEmitido !== null && (vista === 'perfis' || vista === 'tabela');
  // O rascunho (do «Simular» ou da edição) vale em todas as vistas: o depois é ele, o antes é o gravado — ou, com o
  // «Comparar com o emitido», o emitido.
  const r: Resultado | null = it.doRascunho ?? gravado;
  const antes = comparandoDeFato ? doEmitido : it.doRascunho ? gravado : null;
  const semCota = !v || v.cota === null || v.cota === undefined;
  const eixoUsado = semCota ? 'profundidade' : eixo;

  // O cursor começa na leitura do meio da vertical (a primeira encosta nos números do eixo de cima).
  useEffect(() => {
    if (gravado && cursorZ === null && gravado.n > 0) setCursorZ(gravado.col.z[Math.floor(gravado.n / 2)]);
  }, [gravado, cursorZ]);

  // O retângulo do ábaco é de índices do resultado: com outro número de leituras (a edição), ele sai.
  const n = r?.n ?? 0;
  useEffect(() => { setDoAbaco(null); }, [n]);

  // Esc limpa a seleção (o trecho e o retângulo), volta à vertical inteira (o zoom do trecho, onda 347) e fecha o
  // «＋ Trilha» — fora das janelas.
  useEffect(() => {
    const ao = (e: KeyboardEvent) => {
      if (e.key !== 'Escape' || document.querySelector('dialog[open]')) return;
      setSelecao(null);
      setZoom(null);
      setDoAbaco(null);
      setMenu(false);
    };
    window.addEventListener('keydown', ao);
    return () => window.removeEventListener('keydown', ao);
  }, []);

  /*
   * Sair da vertical com rascunho pede confirmação (313.6): o clique num link interno que leva para fora dela (a
   * lista, as abas, o caminho, «Fotos») é segurado antes do roteador e a janela pergunta; trocar de vista na mesma
   * vertical não perde nada. O F5 e o fechar da aba ficam com o aviso do navegador (o molde da dissipação aberta).
   */
  const sujo = it.rascunho !== null || linhasDasCamadas !== null;
  const raizDaVertical = rotaDaVertical(c.campanha.id, verticalId);
  useEffect(() => {
    if (!sujo) return undefined;
    const aoClicar = (e: MouseEvent) => {
      if (e.defaultPrevented || e.button !== 0 || e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) return;
      const a = e.target instanceof Element ? e.target.closest('a[href]') : null;
      if (!(a instanceof HTMLAnchorElement) || (a.target && a.target !== '_self') || a.hasAttribute('download')) return;
      const url = new URL(a.href, window.location.href);
      if (url.origin !== window.location.origin) return;
      if (url.pathname === raizDaVertical || url.pathname.startsWith(`${raizDaVertical}/`)) return;
      e.preventDefault();
      e.stopPropagation();
      setSaida(`${url.pathname}${url.search}${url.hash}`);
    };
    const aoSairDaPagina = (e: BeforeUnloadEvent) => {
      e.preventDefault();
      e.returnValue = '';
    };
    window.addEventListener('click', aoClicar, true);
    window.addEventListener('beforeunload', aoSairDaPagina);
    return () => {
      window.removeEventListener('click', aoClicar, true);
      window.removeEventListener('beforeunload', aoSairDaPagina);
    };
  }, [sujo, raizDaVertical]);

  const mapa = useMemo(() => (r && it.leituras ? mapaDeIndices(r, it.leituras.profundidade.length) : null), [r, it.leituras]);
  const ordem = cursorOrdem ?? (r && mapa && cursorZ !== null ? (mapa.doResultado[indiceNaProfundidade(r.col.z, cursorZ)] ?? -1) : -1);

  /** A leitura do resultado mais perto de z (a seleção e o cursor andam nas leituras de verdade). */
  const naLeitura = (z: number): number => {
    if (!r) return z;
    const i = indiceNaProfundidade(r.col.z, z);
    return i < 0 ? z : r.col.z[i];
  };

  const destacadosDoAbaco = useMemo(() => {
    if (!r) return null;
    if (doAbaco) {
      const u = new Uint8Array(r.n);
      for (const i of doAbaco) if (i >= 0 && i < r.n) u[i] = 1;
      return u;
    }
    if (selecao) {
      const u = new Uint8Array(r.n);
      for (let i = 0; i < r.n; i++) if (r.col.z[i] >= selecao.de - 1e-9 && r.col.z[i] <= selecao.ate + 1e-9) u[i] = 1;
      return u;
    }
    return null;
  }, [r, doAbaco, selecao]);
  const nDestacados = useMemo(() => {
    let k = 0;
    if (destacadosDoAbaco) for (let i = 0; i < destacadosDoAbaco.length; i++) k += destacadosDoAbaco[i];
    return k;
  }, [destacadosDoAbaco]);

  const premissasDoR = r?.premissas ?? null;
  const definicoes = useMemo(() => (parametros === 'correlacionados' && premissasDoR
    ? trilhasDoGrupo(grupo, premissasDoR)
    : OPCOES_DE_TRILHA
      .filter((o) => ligadas.includes(o.chave))
      .map((o) => definicaoDaTrilha(o.chave))
      .filter((d): d is DefinicaoDaTrilha => d !== null)), [ligadas, parametros, grupo, premissasDoR]);

  // A edição que vale (a do rascunho, ou a gravada) e, na vista Edição, o que os cortes e os picos tiram e a
  // correlação cruzada qc × fs — só quando os cortes ou os picos mudam (o deslocamento e os filtros não entram).
  const edicaoValendo = useMemo(() => (v ? (it.rascunho?.edicao ?? edicaoInteira(v.edicao)) : null), [v, it.rascunho]);
  const chaveDaCorrelacao = edicaoValendo ? JSON.stringify([edicaoValendo.cortes, edicaoValendo.picos]) : '';
  const correlacao = useMemo(
    () => (vistaUsada === 'edicao' && it.leituras && edicaoValendo ? correlacaoDaEdicao(it.leituras, edicaoValendo) : null),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [vistaUsada, it.leituras, chaveDaCorrelacao],
  );
  // As camadas da tela: o rascunho, ou as vigentes — conferidas como a API confere.
  const linhasNaTela = useMemo(() => (v ? linhasDasCamadas ?? linhasDasVigentes(v) : []), [v, linhasDasCamadas]);
  const conferencia = useMemo(
    () => conferirCamadas(linhasNaTela, v?.profundidade_final ?? null), [linhasNaTela, v?.profundidade_final]);

  if (it.erro && !v) return <ErroDaApi erro={it.erro} />;
  if (!v || !gravado || !r || !it.leituras) return <Carregando texto="Abrindo a vertical e calculando a interpretação" />;

  const bruto = it.leituras;
  const naDaTela = v.nivel_agua === null && it.rascunho?.nivelAgua === undefined ? null : r.premissas.na;
  const dissipacoes: DissipacaoNaTrilha[] = c.dissipacoes.filter((d) => d.vertical_id === v.id).map((d) => {
    // A marca leva à dissipação aberta (313.5), não só à lista; com a curva lida, os três valores (315).
    const x = daVertical.itens?.find((i) => i.d.id === d.id);
    const valores = x ? (() => {
      const s = interpretar(x, escolhasGravadas(x.d)).s;
      let inicio = NaN;
      for (let k = 0; k < x.curva.u.length; k++) {
        if (Number.isFinite(x.curva.u[k]) && Number.isFinite(x.curva.tempo[k])) { inicio = x.curva.u[k]; break; }
      }
      return { inicio, fim: s.u0, ui: s.ui, extrapolado: s.t50Extrapolado };
    })() : {};
    return {
      z: d.profundidade, rotulo: `${numeroBr(d.profundidade, 2)} m`, para: `/cptu/${c.campanha.id}/dissipacoes/${d.id}`,
      ...valores,
    };
  });
  const camadas = conferencia.lidas.map((k) => ({ topo: k.topo, base: k.base, unidade: k.unidade }));
  const zDoCursor = ordem >= 0 ? bruto.profundidade[ordem] : null;
  const vistaDoSimular = vistaUsada === 'perfis' || vistaUsada === 'tabela';
  // Sem `cptu.editar`, ou com a campanha fora de rascunho/devolvida, o «Simular» fica à vista, bloqueado com o
  // motivo (R8, P-UI-24): é a porta do que muda a interpretação.
  const simularBloqueado = bloqueio === 'permissao'
    ? <AcaoBloqueada texto="Simular" oQueFaz="não simula nem grava a interpretação do CPTu"
      permissao={PERMISSAO_DE_EDITAR_CPTU} persona={eu?.atuandoPor ? eu.nome : null} />
    : bloqueio === 'parada'
      ? <AcaoBloqueada texto="Simular" motivo={motivoDaCampanhaParada(c.campanha.situacao)} />
      : null;

  const alternarCor = () => {
    setColorir((x) => {
      gravarCor(!x);
      return !x;
    });
  };
  // Os básicos e os correlacionados nos Perfis e na Tabela: o mesmo endereço da vista, com a busca de cada um.
  const comBusca = (vv: VistaDaVertical, pp: Parametros = parametros, gg: string = grupo.chave) =>
    `${rotaDaVertical(c.campanha.id, v.id, vv)}${vv === 'perfis' || vv === 'tabela' ? buscaDosParametros(pp, gg) : ''}`;
  const correlacionados = parametros === 'correlacionados' && vistaDoSimular;
  // «Comparar com o emitido» (315): à vista no computador, nos Perfis e na Tabela; sem emissão, bloqueado com o motivo.
  const motivoDoComparar = emitido.estado === 'sem' ? SEM_EMISSAO
    : emitido.estado === 'erro' ? 'O emitido não pôde ser lido agora (veja o erro acima).' : null;
  const faixaDoEmitido = comparandoDeFato && emitido.e ? (
    <Aviso tipo="info" titulo={`Comparando com o emitido ${revisaoEscrita(emitido.e.revisao)} de ${data(emitido.e.emitidoEm)}`}>
      <p>
        O antes é o relatório {emitido.e.numero} · {revisaoEscrita(emitido.e.revisao)}, recalculado das leituras, das premissas
        e da edição do pacote emitido; o depois é o de agora{it.rascunho ? ' (com o rascunho)' : ''}. As trilhas e o ábaco
        mostram o emitido em cinza tracejado, e a leitura na cota escreve «emitido → agora». Nada é gravado.
      </p>
      {emitido.e.motor !== MOTOR_CPTU_VERSAO && (
        <p>O emitido foi calculado com o motor {emitido.e.motor}; a comparação usa o motor {MOTOR_CPTU_VERSAO}.</p>
      )}
    </Aviso>
  ) : null;

  const alternarTrilha = (chave: string) => {
    const novas = ligadas.includes(chave) ? ligadas.filter((x) => x !== chave) : [...ligadas, chave];
    setLigadas(novas);
    gravarTrilhas(novas);
  };
  const limpar = () => { setSelecao(null); setZoom(null); setDoAbaco(null); };

  const aoGravado = async (resto: Rascunho | null) => {
    it.simular(resto);
    if (!resto) setSimulando(false);
    await it.recarregar();
    await aoMudar();
  };
  const aoGravarAsCamadas = async () => {
    setLinhasDasCamadas(null);
    setSugestao(null);
    await it.recarregar();
    await aoMudar();
  };

  // A faixa do trecho (ou do retângulo do ábaco): «Limpar» a tira e devolve o foco ao desenho (I2).
  const limparPeloBotao = () => { limpar(); focarOPrimeiro('.cptu-trilhas', '.cptu-tabela-caixa'); };
  const faixasDoTrecho = (
    <>
      {selecao && (
        <p className="cptu-faixa-trecho" role="status">
          <b>Trecho {faixaProfundidade(selecao.de, selecao.ate)} m</b> · {contaDeLeituras(nDestacados)} — os mesmos
          pontos acendem no ábaco.{' '}
          <button type="button" className="ligacao" onClick={limparPeloBotao}>Limpar (Esc)</button>
        </p>
      )}
      {doAbaco && (
        <p className="cptu-faixa-trecho" role="status">
          <b>Ábaco {desenhoDoAbaco(tipo).titulo}</b>: {contaDeLeituras(nDestacados)} no retângulo — as profundidades
          marcadas nas trilhas.{' '}
          <button type="button" className="ligacao" onClick={limparPeloBotao}>Limpar (Esc)</button>
        </p>
      )}
    </>
  );

  // A faixa do rascunho (313.6): fora da vista que o mexe — e com o «Simular» fechado —, o que está no rascunho e
  // «Sair sem gravar»; idem para o rascunho das camadas fora da vista Camadas.
  const noRascunho = oQueHaNoRascunho(it.rascunho);
  const faixaDoRascunho = !estreito && it.rascunho && vistaUsada !== 'edicao' && !(simulando && vistaDoSimular);
  const faixaDasCamadas = !estreito && linhasDasCamadas !== null && vistaUsada !== 'camadas';

  return (
    <section className="cptu-vertical-tela" aria-label={`Vertical ${v.codigo}`}>
      {/* No celular, e nas vistas Edição e Camadas (o formulário pede o lado e as trilhas, a coluna das unidades), a
          lista das verticais vira a escolha no topo; trocar de vertical com rascunho pergunta, como o link. */}
      {estreito || comFormulario ? (
        <label className="campo cptu-escolha-vertical">
          <span className="rotulo">Vertical</span>
          <select value={v.id} onChange={(e) => {
            const para = rotaDaVertical(c.campanha.id, e.target.value, vistaUsada);
            if (sujo) setSaida(para); else navegar(para);
          }}>
            {c.verticais.map((x) => <option key={x.id} value={x.id}>{x.codigo}</option>)}
          </select>
        </label>
      ) : (
        <ListaDasVerticais c={c} aberta={v.id} vista={vistaUsada}
          busca={vistaDoSimular ? buscaDosParametros(parametros, grupo.chave) : ''} />
      )}

      <div className="cptu-centro">
        <CabecalhoDaVertical c={c} v={v} a={r.premissas.a} />
        {/* A releitura que falha depois da primeira não cala: o que está na tela pode estar velho. */}
        {it.erro ? <ErroDaApi erro={it.erro} /> : null}
        {estreito && <Aviso tipo="info">Premissas, edição, camadas e dissipações se mudam no computador.</Aviso>}
        <div className="cptu-barra-vertical">
          {vistaUsada !== 'tabela' && (
            <>
              <div className="cptu-segmento" role="group" aria-label="Eixo vertical">
                <button type="button" className="cptu-segmento-opcao" aria-pressed={eixoUsado === 'profundidade'}
                  onClick={() => setEixo('profundidade')}>
                  Profundidade
                </button>
                {/* Sem cota, o botão fica alcançável pelo teclado, com o motivo dito (P-UI-25). */}
                <button type="button" className="cptu-segmento-opcao" aria-pressed={eixoUsado === 'cota'}
                  aria-disabled={semCota || undefined} aria-describedby={semCota ? idDoMotivo : undefined}
                  onClick={() => { if (!semCota) setEixo('cota'); }}>
                  Cota
                </button>
              </div>
              {semCota && (
                <small id={idDoMotivo} className="cptu-motivo-bloqueio">
                  A vertical {v.codigo} não tem cota: informe-a no cabeçalho, na ficha da vertical.
                </small>
              )}
              {!correlacionados && (
                <button type="button" className="botao secundario" aria-expanded={menu} onClick={() => setMenu((x) => !x)}>
                  ＋ Trilha
                </button>
              )}
              <button type="button" className="botao secundario cptu-colorir" aria-pressed={colorir} onClick={alternarCor}>
                Colorir pela zona{colorir ? ': ligado' : ': desligado'}
              </button>
            </>
          )}
          {!estreito && vistaDoSimular && (motivoDoComparar
            ? <AcaoBloqueada texto="Comparar com o emitido" motivo={motivoDoComparar} />
            : (
              <button type="button" className="botao secundario cptu-comparar" aria-pressed={comparando}
                aria-busy={emitido.estado === 'lendo' || undefined}
                onClick={() => { if (emitido.estado === 'ok') setComparando((x) => !x); }}>
                {comparando ? 'Sair da comparação' : 'Comparar com o emitido'}
              </button>
            ))}
          {!estreito && vistaDoSimular && (simularBloqueado ?? (
            <button type="button" className="botao secundario" data-abre="simular" onClick={() => setSimulando(true)}>
              Simular
            </button>
          ))}
          <nav className="cptu-vistas" aria-label="Vistas da vertical">
            {([['perfis', 'Perfis'], ['tabela', 'Tabela'], ['edicao', 'Edição'], ['camadas', 'Camadas']] as const).map(([vv, texto]) => (
              <Link key={vv} to={comBusca(vv)} aria-current={vistaUsada === vv ? 'page' : undefined}
                className={vistaUsada === vv ? 'sel' : undefined}>
                {texto}
              </Link>
            ))}
          </nav>
        </div>
        {vistaDoSimular && (
          <nav className="cptu-parametros" aria-label="Parâmetros">
            {([['basicos', 'Parâmetros básicos'], ['correlacionados', 'Parâmetros correlacionados']] as const).map(([pp, texto]) => (
              <Link key={pp} to={comBusca(vistaUsada, pp)} aria-current={parametros === pp ? 'page' : undefined}
                className={parametros === pp ? 'sel' : undefined}>
                {texto}
              </Link>
            ))}
          </nav>
        )}
        {correlacionados && vistaUsada === 'perfis' && (
          <nav className="cptu-grupos" aria-label="Grupos dos parâmetros correlacionados">
            {GRUPOS_CORRELACIONADOS.map((g) => (
              <Link key={g.chave} to={comBusca('perfis', 'correlacionados', g.chave)}
                aria-current={grupo.chave === g.chave ? 'page' : undefined} className={grupo.chave === g.chave ? 'sel' : undefined}>
                {g.titulo}
              </Link>
            ))}
          </nav>
        )}
        {emitido.estado === 'erro' && !estreito && vistaDoSimular ? <ErroDaApi erro={emitido.erro} /> : null}
        {faixaDoEmitido}
        {menu && vistaUsada !== 'tabela' && !correlacionados && (
          <MenuDasTrilhas r={r} ligadas={ligadas} aoAlternar={alternarTrilha} aoFechar={() => setMenu(false)} />
        )}
        {simulando && vistaDoSimular && !estreito && !simularBloqueado && (
          <SimularPremissas c={c} vertical={v} gravadas={gravado.premissas} rascunho={it.rascunho}
            aoMudar={it.simular} aoGravado={aoGravado}
            aoDescartar={() => { it.simular(null); setSimulando(false); }} />
        )}
        {faixaDoRascunho && (
          <Aviso tipo="atencao" titulo="Rascunho não gravado">
            <p>
              No rascunho: {noRascunho.join(', ')}. As trilhas, a leitura, a tabela e o ábaco mostram o depois; o gravado
              vai em cinza tracejado.
            </p>
            <div className="cptu-faixa-rascunho-acoes">
              {(it.rascunho?.edicao !== undefined || it.rascunho?.premissas?.u0Modo !== undefined
                || it.rascunho?.premissas?.u0Pontos !== undefined) && (
                <Link className="botao secundario" to={rotaDaVertical(c.campanha.id, v.id, 'edicao')}>Abrir a edição</Link>
              )}
              <button type="button" className="botao secundario" onClick={() => {
                it.simular(null);
                setSimulando(false);
                // A faixa some: o foco vai ao desenho (ou à tabela), o próximo controle — nunca ao <body> (I2).
                focarOPrimeiro('.cptu-trilhas', '.cptu-tabela-caixa');
              }}>
                Sair sem gravar o rascunho
              </button>
            </div>
          </Aviso>
        )}
        {faixaDasCamadas && (
          <Aviso tipo="atencao" titulo="As camadas têm rascunho não gravado">
            <p>As trilhas desenham o rascunho, não as vigentes.</p>
            <div className="cptu-faixa-rascunho-acoes">
              <Link className="botao secundario" to={rotaDaVertical(c.campanha.id, v.id, 'camadas')}>Abrir as camadas</Link>
              <button type="button" className="botao secundario" onClick={() => {
                setLinhasDasCamadas(null);
                focarOPrimeiro('.cptu-trilhas', '.cptu-tabela-caixa');
              }}>
                Sair sem gravar as camadas
              </button>
            </div>
          </Aviso>
        )}
        {r.avisos.length > 0 && (
          <details className="cptu-avisos-motor">
            <summary>{plural(r.avisos.length, 'aviso do motor', 'avisos do motor')}</summary>
            <ul>{r.avisos.map((a) => <li key={a}>{a}</li>)}</ul>
          </details>
        )}
        {vistaUsada === 'tabela' ? (
          <>
            {faixasDoTrecho}
            <TabelaDaVertical key={parametros} modo={parametros} bruto={bruto} r={r} ordemDoCursor={ordem >= 0 ? ordem : null}
              aoEscolher={(k) => {
                setCursorOrdem(k);
                const z = bruto.profundidade[k];
                if (Number.isFinite(z)) setCursorZ(z);
              }} />
          </>
        ) : (
          // A faixa do trecho vai por cima do cabeçalho das trilhas: o desenho não pula embaixo do ponteiro quando ela
          // entra ou sai (rodada do agent-browser da 313).
          <div className="cptu-area-das-trilhas">
          {faixasDoTrecho}
          <Trilhas
            r={r}
            antes={antes}
            trilhas={definicoes}
            eixo={eixoUsado}
            cota={v.cota ?? null}
            cursor={cursorZ}
            aoCursor={(z) => { setCursorOrdem(null); setCursorZ(z); }}
            selecao={selecao}
            aoSelecionar={(s) => {
              setDoAbaco(null);
              if (!s) { setSelecao(null); return; }
              const de = naLeitura(s.de);
              const ate = naLeitura(s.ate);
              setSelecao({ de: Math.min(de, ate), ate: Math.max(de, ate) });
            }}
            destacados={doAbaco ? destacadosDoAbaco : null}
            na={naDaTela}
            dissipacoes={dissipacoes}
            camadas={camadas}
            sugestoes={vistaUsada === 'camadas' && sugestao ? sugestao.s.fronteiras : undefined}
            aoNavegar={(para) => navegar(para)}
            altura={altura}
            colorirPorZona={colorir}
            escalas={escalas}
            aoMudarEscala={mudarEscala}
            zoom={zoom}
            aoZoom={setZoom}
          />
          {correlacionados && grupo.nota ? <p className="cptu-nota cptu-nota-do-grupo">{grupo.nota}</p> : null}
          </div>
        )}
        {vistaUsada === 'edicao' && correlacao && !estreito && edicaoValendo && (
          <CorrelacaoCruzadaGrafico correlacao={correlacao} atualCm={edicaoValendo.deslocamento.fs} />
        )}
        {vistaUsada === 'camadas' && (
          <>
            <TabelaDasCamadas linhas={linhasNaTela} conferencia={conferencia} soLer={soLer}
              aoMudar={(l) => setLinhasDasCamadas(l)} />
            <EstatisticaDasCamadas r={r} lidas={conferencia.lidas} sobreRascunho={antes !== null} />
          </>
        )}
      </div>

      {/* O lado da edição é mais largo (os campos lado a lado); o das camadas é o de sempre, para as trilhas e a coluna
          das unidades caberem numa linha. */}
      <aside className={`cptu-lado${vistaUsada === 'edicao' ? ' cptu-lado-largo' : ''}`}
        aria-label={vistaUsada === 'edicao' ? 'A edição e a leitura na cota'
          : vistaUsada === 'camadas' ? 'As camadas e a leitura na cota' : 'Na profundidade e o ábaco'}>
        {vistaUsada === 'edicao' && (
          <EdicaoDaVertical c={c} v={v} gravadas={gravado.premissas} rascunho={it.rascunho} correlacao={correlacao}
            estreito={estreito} bloqueio={bloqueio} aoMudar={it.simular} aoGravado={aoGravado} />
        )}
        {vistaUsada === 'camadas' && (
          <CamadasDoLado c={c} v={v} r={r} cursorZ={cursorZ} linhas={linhasDasCamadas} atuais={linhasNaTela} conferencia={conferencia}
            sugestao={sugestao?.s ?? null} sugestaoSobreRascunho={sugestao?.sobreRascunho ?? false} estreito={estreito}
            bloqueio={bloqueio} aoMudar={setLinhasDasCamadas}
            // A sugestão é sobre o que a tela mostra: o resultado do rascunho, quando há (o antes é o gravado).
            aoSugerir={(s) => setSugestao(s ? { s, sobreRascunho: antes !== null } : null)} aoGravado={aoGravarAsCamadas}
            escalas={escalas} aoMudarEscala={mudarEscala} sobreRascunho={antes !== null} />
        )}
        {eixoUsado === 'cota' && zDoCursor !== null && v.cota !== null && vistaUsada !== 'tabela' && (
          <p className="cptu-cota-da-leitura">cota {numeroBr(v.cota - zDoCursor, 2)} m (boca a {numeroBr(v.cota, 2)} m)</p>
        )}
        {ordem >= 0 && (
          <div className="cptu-leitura-caixa">
            <LeituraNaCota bruto={bruto} r={r} ordem={ordem} antes={antes} />
          </div>
        )}
        {!comFormulario && (
          <>
            <div className="cptu-escolha-abaco" role="group" aria-label="Ábaco">
              {ABACOS.map(([t, texto]) => (
                <button key={t} type="button" className="cptu-abaco-opcao" aria-pressed={tipo === t}
                  onClick={() => { setTipo(t); setDoAbaco(null); }}>
                  {texto}
                </button>
              ))}
            </div>
            <Abaco tipo={tipo} r={r} antes={antes} destacados={destacadosDoAbaco}
              aoSelecionar={estreito ? undefined : (ind) => {
                setSelecao(null);
                setDoAbaco(ind && ind.length > 0 ? ind : null);
              }} />
          </>
        )}
      </aside>
      {saida && (
        <Dialogo aberto titulo="Sair sem gravar?" rotulo={`Vertical ${v.codigo}`} aoFechar={() => setSaida(null)}>
          <p className="sub">
            A vertical {v.codigo} tem rascunho não gravado
            {[...noRascunho, ...(linhasDasCamadas !== null ? ['as camadas'] : [])].length > 0
              ? ` (${[...noRascunho, ...(linhasDasCamadas !== null ? ['as camadas'] : [])].join(', ')})` : ''}. Saindo, ele
            se perde.
          </p>
          <div className="rodape-dialogo">
            <button type="button" className="botao perigo" onClick={() => {
              const para = saida;
              setSaida(null);
              it.simular(null);
              setLinhasDasCamadas(null);
              navegar(para);
            }}>Sair sem gravar</button>
            <button type="button" className="botao secundario" onClick={() => setSaida(null)}>Cancelar</button>
          </div>
        </Dialogo>
      )}
    </section>
  );
}

/** O que o rascunho da interpretação tem, escrito: «a edição», «o u₀ por pontos», «o NA», «as premissas (Nkt)». */
function oQueHaNoRascunho(rascunho: Rascunho | null): string[] {
  if (!rascunho) return [];
  const partes: string[] = [];
  if (rascunho.edicao !== undefined) partes.push('a edição');
  const premissas = Object.keys(rascunho.premissas ?? {}) as (keyof NonNullable<Rascunho['premissas']>)[];
  if (premissas.includes('u0Modo') || premissas.includes('u0Pontos')) partes.push('o u₀');
  if (rascunho.nivelAgua !== undefined) partes.push('o NA');
  if (rascunho.cota !== undefined) partes.push('a cota');
  const outras = premissas.filter((k) => k !== 'u0Modo' && k !== 'u0Pontos');
  if (outras.length > 0) {
    partes.push(`as premissas (${outras.map((k) => FORMULAS_DAS_PREMISSAS[k]?.rotulo ?? k).join(', ')})`);
  }
  return partes;
}

/** As verticais da campanha, a aberta marcada (a prancheta: código, profundidade, faixa SBTn, NA…). */
function ListaDasVerticais({ c, aberta, vista, busca }: {
  c: CampanhaCompleta; aberta: string; vista: VistaDaVertical; busca: string;
}) {
  const naCampanha = (d: VerticalDaCampanha) => c.dissipacoes.filter((x) => x.vertical_id === d.id).length;
  return (
    <nav className="cptu-lista-verticais" aria-label="Verticais da campanha">
      <h2 className="cptu-lista-titulo">Verticais</h2>
      {c.verticais.map((x) => {
        const excecoes = Object.keys(excecoesDaVertical(x)).length;
        const dis = naCampanha(x);
        const partes = [
          x.nivel_agua !== null ? `NA ${numeroBr(x.nivel_agua, 2)} m` : 'NA não informado',
          dis > 0 ? plural(dis, 'dissipação', 'dissipações') : null,
          excecoes > 0 ? plural(excecoes, 'exceção', 'exceções') : null,
          x.n_camadas > 0 ? plural(x.n_camadas, 'camada', 'camadas') : null,
          x.pre_furo_m ? `pré-furo ${numeroBr(x.pre_furo_m, 2)} m` : null,
        ].filter(Boolean);
        return (
          <Link key={x.id} className="cptu-item-vertical" to={`${rotaDaVertical(c.campanha.id, x.id, vista)}${busca}`}
            aria-current={x.id === aberta ? 'page' : undefined}>
            <span className="cptu-item-topo">
              <b className="mono">{x.codigo}</b>{' '}
              <span className="mono">{m2(x.profundidade_final) ?? '—'}</span>
            </span>
            <FaixaSbtn verticalId={x.id} codigo={x.codigo} />
            <span className="cptu-item-resumo">{partes.join(' · ')}</span>
          </Link>
        );
      })}
    </nav>
  );
}

/** Quantos trechos a faixa da lista desenha, no máximo (é um retrato da vertical, não o perfil). */
const TRECHOS_DA_FAIXA = 120;

/** A faixa SBTn de uma vertical já aberta nesta sessão: a zona de cada trecho, de cima para baixo. */
function FaixaSbtn({ verticalId, codigo }: { verticalId: string; codigo: string }) {
  const zonas = zonasDaSessao(verticalId);
  if (!zonas || zonas.z.length === 0) return null;
  const n = zonas.z.length;
  const z0 = zonas.z[0];
  const z1 = zonas.z[n - 1];
  const passo = (z1 - z0) / TRECHOS_DA_FAIXA || 1;
  // A zona mais frequente de cada trecho de igual profundidade; trechos seguidos da mesma zona se juntam.
  const trechos: { zona: number; peso: number }[] = [];
  let i = 0;
  for (let t = 0; t < TRECHOS_DA_FAIXA && i < n; t++) {
    const fim = z0 + (t + 1) * passo;
    const conta = new Map<number, number>();
    while (i < n && (zonas.z[i] < fim || t === TRECHOS_DA_FAIXA - 1)) {
      conta.set(zonas.sbtn[i], (conta.get(zonas.sbtn[i]) ?? 0) + 1);
      i++;
    }
    let zona = 0;
    let mais = -1;
    for (const [k, q] of conta) if (q > mais) { zona = k; mais = q; }
    const ultimo = trechos[trechos.length - 1];
    if (ultimo && ultimo.zona === zona) ultimo.peso++;
    else trechos.push({ zona, peso: 1 });
  }
  const zonasDoMotor = ZONAS_SBTN as Readonly<Record<number, { token: string; nome: string; nomeCurto: string }>>;
  // A zona escrita junto da cor (P-UI-15): as três que mais ocupam a vertical, com a parte de cada uma.
  const porZona = new Map<number, number>();
  for (let k = 0; k < n; k++) if (zonasDoMotor[zonas.sbtn[k]]) porZona.set(zonas.sbtn[k], (porZona.get(zonas.sbtn[k]) ?? 0) + 1);
  const principais = [...porZona.entries()].sort((x, y) => y[1] - x[1]).slice(0, 3)
    .map(([zona, q]) => `${zona} ${zonasDoMotor[zona].nomeCurto} ${numero((100 * q) / n, 0)} %`);
  return (
    <span className="cptu-faixa">
      <span className="cptu-faixa-sbtn" aria-hidden="true">
        {trechos.map((t, k) => {
          const zona = zonasDoMotor[t.zona];
          return <span key={k} style={{ flexGrow: t.peso, background: zona ? `var(${zona.token})` : undefined }} />;
        })}
      </span>
      <span className="cptu-faixa-legenda">
        <span className="so-leitor">Zonas SBTn de {codigo}: </span>SBTn {principais.join(' · ')}
      </span>
    </span>
  );
}

/** O cabeçalho da vertical: data, operador, o cone (apto na data do ensaio, ou o impedimento), cota, NA, encerramento. */
function CabecalhoDaVertical({ c, v, a }: { c: CampanhaCompleta; v: VerticalCompleta; a: number }) {
  // Sem a data, o cone do parque já a diz («sem data do ensaio: apto hoje»): a frase sai uma vez só.
  const partes = [
    v.data_ensaio ? dataDoDia(v.data_ensaio) : v.cone_equipamento_id ? null : 'sem data do ensaio',
    v.operador,
    textoDoCone(v, a),
    v.cota !== null && v.cota !== undefined ? `cota ${v.cota >= 0 ? '+' : ''}${numeroBr(v.cota, 2)} m` : 'sem cota',
    v.nivel_agua !== null ? `NA ${numeroBr(v.nivel_agua, 2)} m` : 'NA não informado',
    textoDoEncerramento(v.motivo_parada, v.motivo_parada_texto),
  ].filter(Boolean);
  return (
    <header className="cptu-cabeca-vertical">
      <h2 className="mono">{v.codigo}</h2>
      <p className="cptu-cabeca-detalhes">
        {partes.join(' · ')}
        {' · '}
        <Link className="cptu-fotos-da-vertical" to={`/cptu/${c.campanha.id}?ficha=${v.id}`}>Fotos ({v.fotos.length})</Link>
      </p>
    </header>
  );
}

/**
 * O cone como o cabeçalho o diz: o do parque (com a área e o a) e se estava apto na data do ensaio — sem a
 * data, o de hoje (revisão da 313.2) —, ou o número do arquivo que o parque não conhece, num parêntese só:
 * «cone 1378 (fora de Equipamentos · 10 cm², a = 0,80)».
 */
function textoDoCone(v: VerticalCompleta, a: number): string {
  /* Onda 335: o cone achado pelo número do arquivo (sem cone gravado) conta como o do parque, e a origem se diz. */
  const doParque = !!v.cone_equipamento_id || !!v.cone_pelo_numero;
  const nome = doParque
    ? [v.cone_patrimonio, v.cone_nome].filter(Boolean).join(' · ')
    : v.cone_codigo;
  if (!nome) return 'cone não informado';
  const medidas = [v.area_cone_cm2 ? `${numero(v.area_cone_cm2, 0)} cm²` : null, `a = ${numeroBr(a, 2)}`].filter(Boolean).join(', ');
  const geometria = !doParque ? `fora de Equipamentos · ${medidas}`
    : v.cone_equipamento_id ? medidas : `achado pelo nº ${v.cone_codigo} do arquivo · ${medidas}`;
  let situacao = '';
  if (doParque) {
    if (v.data_ensaio) situacao = v.cone_impedimento ? ` · impedido: ${v.cone_impedimento}` : ` · apto em ${dataDoDia(v.data_ensaio)}`;
    else situacao = v.cone_impedimento ? ` · sem data do ensaio: ${v.cone_impedimento}` : ' · sem data do ensaio: apto hoje';
  }
  return `cone ${nome} (${geometria})${situacao}`;
}

/** O «＋ Trilha»: as cinco padrão e as grandezas do motor, cada uma com a unidade e a marca de homologação. */
function MenuDasTrilhas({ r, ligadas, aoAlternar, aoFechar }: {
  r: Resultado; ligadas: readonly string[]; aoAlternar(chave: string): void; aoFechar(): void;
}) {
  // O rótulo da série só entra no porquê quando a trilha tem mais de uma (o u₂ com o u₀): assim as trilhas de uma
  // série que saem pela mesma condição dividem a mesma nota.
  const marcaDe = (o: OpcaoDeTrilha): MarcaDeHomologacao => combinarMarcas(o.series.map((s) => ({
    marca: marcaDaColuna(s, r.premissas), rotulo: o.series.length > 1 ? GRANDEZAS[s].rotulo : undefined,
  })));
  const opcoes = OPCOES_DE_TRILHA.map((o) => ({ o, m: marcaDe(o) })).filter((x) => x.m.mostrar);
  // O porquê de cada «▲» numa nota numerada à vista, uma vez cada (o `title` nunca é o único caminho, P-UI-44).
  const motivos: string[] = [];
  for (const { m } of opcoes) if (!m.homologada && m.motivo && !motivos.includes(m.motivo)) motivos.push(m.motivo);
  return (
    <fieldset className="cptu-menu-trilhas">
      <legend>As trilhas (a escolha fica neste aparelho)</legend>
      <div className="cptu-opcoes-de-trilha">
        {opcoes.map(({ o, m }) => (
          <label key={o.chave} className="cptu-opcao">
            <input type="checkbox" checked={ligadas.includes(o.chave)} onChange={() => aoAlternar(o.chave)} />
            <span className="cptu-opcao-rotulo">{o.rotulo}</span>
            {o.detalhe && <span className="cptu-nota"> {o.detalhe}</span>}
            {!m.homologada && m.motivo && (
              <span className="cptu-marca-texto"> ▲ ({motivos.indexOf(m.motivo) + 1})</span>
            )}
          </label>
        ))}
      </div>
      <NotasDoPorque motivos={motivos} />
      <button type="button" className="ligacao" onClick={aoFechar}>Fechar</button>
    </fieldset>
  );
}
